import React, { useState, useEffect } from "react";

const SendMessageForm = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    countryCode: "+91",
    phone: "",
    company: "",
    purpose: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isPurposeLocked, setIsPurposeLocked] = useState(false);

  const purposeOptions = [
    "General Inquiry",
    "Platform Demo",
    "Technical Support",
    "Partnership Opportunity",
    "Sales Inquiry",
    "Other",
  ];

  const platformOptions = [
    "Women Health Platform",
    "Child Health Platform",
    "Behavioral Health Platform",
    "Health Concierge Platform​",
    "Second Opinion Platform​",
  ];

  const countryCodes = [
    { code: "+1", country: "US/CA" },
    { code: "+44", country: "UK" },
    { code: "+91", country: "IN" },
    // { code: '+64', country: 'NZ' },
  ];

  // Prefill purpose from URL query parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const purposeParam = urlParams.get("purpose");

    if (purposeParam && purposeOptions.includes(purposeParam)) {
      setFormData((prev) => ({
        ...prev,
        purpose: purposeParam,
      }));
      setIsPurposeLocked(true); // Lock the field when prefilled
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Prevent changing purpose if it's locked
    if (name === "purpose" && isPurposeLocked) {
      return;
    }

    // Reset platform selection when purpose changes and is not Platform Demo
    if (name === "purpose" && value !== "Platform Demo") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        platform: "",
      }));
      // Special handling for phone number - only allow digits
    } else if (name === "phone") {
      const numbersOnly = value.replace(/\D/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: numbersOnly,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Phone number validation - must be exactly 10 digits if provided
    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (formData.phone.length !== 10) {
      newErrors.phone = "Phone number must be exactly 10 digits";
    }

    if (!formData.purpose) {
      newErrors.purpose = "Please select a purpose";
    }

    // Platform validation - required only when purpose is Platform Demo
    if (formData.purpose === "Platform Demo" && !formData.platform) {
      newErrors.platform = "Please select a platform";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const submissionData = {
        ...formData,
        fullPhone: formData.phone
          ? `${formData.countryCode} ${formData.phone}`
          : "",
      };
      // console.log('Form submitted:', submissionData);
      alert(
        "Message sent successfully! We will get back to you within 24 hours.",
      );

      setFormData({
        fullName: "",
        email: "",
        countryCode: "+91",
        phone: "",
        company: "",
        purpose: "",
        platform: "",
        message: "",
      });
      setIsPurposeLocked(false); // Reset lock after submission
    }
  };

  return (
    <section className="primary-bg py-12 md:py-16 lg:py-20">
      <div className="mx-auto px-4 md:px-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10 lg:mb-12">
          <h2 className=" font-bold text-white mb-3 md:mb-4">
            Send Us a <span className="secondary-text">Message</span>
          </h2>
          <p className="text-white opacity-90">
            Fill out the form below and we'll get back to you within 24 hours
          </p>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Full Name and Email Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <div>
              <label
                htmlFor="fullName"
                className="block text-white font-medium mb-2"
              >
                Full Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className={`w-full px-4 py-2 rounded-lg bg-transparent border ${
                  errors.fullName ? "border-red-400" : "border-white"
                } text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors duration-300`}
              />
              {errors.fullName && (
                <p className="text-red-400  mt-1">{errors.fullName}</p>
              )}
            </div>

            {/* Email Address */}
            <div>
              <label
                htmlFor="email"
                className="block text-white font-medium mb-2"
              >
                Email Address <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-2 rounded-lg bg-transparent border ${
                  errors.email ? "border-red-400" : "border-white"
                } text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors duration-300`}
              />
              {errors.email && (
                <p className="text-red-400 mt-1">{errors.email}</p>
              )}
            </div>
          </div>

          {/* Phone Number and Company Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Phone Number */}
            <div>
              <label
                htmlFor="phone"
                className="block text-white font-medium mb-2"
              >
                Phone Number <span className="text-red-400">*</span>
              </label>
              <div className="flex gap-2">
                {/* Country Code Dropdown */}
                <div className="relative w-28">
                  <select
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    className="w-full px-2 py-2 rounded-lg bg-transparent border border-white text-white focus:outline-none focus:border-white transition-colors duration-300 appearance-none cursor-pointer "
                  >
                    {countryCodes.map((item) => (
                      <option
                        key={item.code}
                        value={item.code}
                        className="bg-gray-800"
                      >
                        {item.code} ({item.country})
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>

                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`flex-1 px-4 py-2 rounded-lg bg-transparent ${
                    errors.phone ? "border-red-400" : "border-white"
                  }  border text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors duration-300`}
                  maxLength="10"
                />
              </div>
              {errors.phone && (
                <p className="text-red-400 mt-1">{errors.phone}</p>
              )}
            </div>

            {/* Company/Organization */}
            <div>
              <label
                htmlFor="company"
                className="block text-white font-medium mb-2"
              >
                Company/Organization
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Enter your Company Name"
                className="w-full px-4 py-2 rounded-lg bg-transparent border border-white text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors duration-300"
              />
            </div>
          </div>

          {/* Purpose of Contact */}
          <div>
            <label
              htmlFor="purpose"
              className="block text-white font-medium mb-2"
            >
              Purpose of Contact <span className="text-red-400">*</span>
              {/* {isPurposeLocked && (
                <span className="ml-2 text-xs text-gray-300">(Pre-selected)</span>
              )} */}
            </label>
            <div className="relative">
              <select
                id="purpose"
                name="purpose"
                value={formData.purpose}
                onChange={handleChange}
                disabled={isPurposeLocked}
                className={`w-full px-4 py-2 rounded-lg bg-transparent border ${
                  errors.purpose ? "border-red-400" : "border-white"
                } text-white focus:outline-none focus:border-white transition-colors duration-300 appearance-none ${
                  isPurposeLocked
                    ? "cursor-not-allowed opacity-75"
                    : "cursor-pointer"
                }`}
              >
                <option value="" disabled className="bg-gray-800">
                  Select your Purpose
                </option>
                {purposeOptions.map((option) => (
                  <option key={option} value={option} className="bg-gray-800">
                    {option}
                  </option>
                ))}
              </select>
              {!isPurposeLocked && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              )}
            </div>
            {errors.purpose && (
              <p className="text-red-400 mt-1">{errors.purpose}</p>
            )}
          </div>

          {/* Platform Selection - Conditional Field */}
          {formData.purpose === "Platform Demo" && (
            <div className="transition-all duration-300 ease-in-out">
              <label
                htmlFor="platform"
                className="block text-whitefont-medium mb-2"
              >
                Select Platform <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  id="platform"
                  name="platform"
                  value={formData.platform}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg bg-transparent border ${
                    errors.platform ? "border-red-400" : "border-white"
                  } text-white focus:outline-none focus:border-white transition-colors duration-300 appearance-none cursor-pointer`}
                >
                  <option value="" disabled className="bg-gray-800">
                    Select Platform
                  </option>
                  {platformOptions.map((option) => (
                    <option key={option} value={option} className="bg-gray-800">
                      {option}
                    </option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
              {errors.platform && (
                <p className="text-red-400 mt-1">{errors.platform}</p>
              )}
            </div>
          )}

          {/* Message */}
          <div>
            <label
              htmlFor="message"
              className="block text-white font-medium mb-2"
            >
              Message <span className="text-red-400">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your inquiry or How we can help you..."
              rows="5"
              className={`w-full px-4 py-2 rounded-lg bg-transparent border ${
                errors.message ? "border-red-400" : "border-white"
              } text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors duration-300 resize-none`}
            />
            {errors.message && (
              <p className="text-red-400 mt-1">{errors.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <div>
            <button onClick={handleSubmit} className="btn btn-white">
              Send Message
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SendMessageForm;
